import {
	buildChunkManifest,
	headerFirstChunkOrder,
	readChunkedObjectOfferKillSwitch,
	shouldOfferChunkedObject,
	type ChunkManifest
} from "./chunkManifest";
import { CHUNK_SIZE } from "./chunkSize";
import {
	blobByteReader,
	ISOBMFF_LAYOUT_TIMEOUT_MS,
	locateIsobmffLayout,
	type IsobmffLayout,
	type IsobmffLayoutResult
} from "./isobmffLayout";
import type { UploadTelemetry } from "./ports";
import { withPickedFileReadTurn } from "./pickedFileReadGate";

/**
 * Everything a caller needs to OFFER one selected video file as a `chunked-object` upload
 * (spec 2026-09-04 §Transport; plan 3a decision D3). Shared by contact-async and contact-admin —
 * the two apps must decide identically, or the same file would be offered on one and not the other.
 */
export interface ChunkedObjectVideoIntent {
	manifest: ChunkManifest;
	layout: IsobmffLayout;
	/** 0-based chunk indexes in upload priority order (header-first). A full permutation of the parts. */
	priorityChunkIndexes: number[];
	chunkSizeBytes: number;
	/**
	 * The kill-switch reading `derivePlan` ACTUALLY gated this offer on, carried forward so the app
	 * initiators can stamp it on `upload.chunkedObject.notSelected` instead of taking a second,
	 * later reading and presenting it as the same fact.
	 *
	 * Structurally `false` whenever an intent exists — the switch returns null before anything is
	 * planned — and that is the point: a `notSelected` line that re-reads the switch at target-creation
	 * time is reporting a DIFFERENT reading, so the two must be reported as two fields. A disagreement
	 * between them is an operator flipping the switch mid-upload-start, which was previously
	 * indistinguishable from the ordinary case.
	 */
	killSwitchDisabled: boolean;
}

/**
 * Wall-clock budget for the WHOLE plan, not for the box walk's own between-reads budget.
 *
 * `locateIsobmffLayout` checks its deadline BETWEEN reads, so it cannot interrupt a single
 * `Blob.slice().arrayBuffer()` that never settles — which is exactly what an iCloud/OneDrive
 * placeholder file does when the OS declines to hydrate it. Without this race the upload would
 * never start at all. Slightly above the walk's own budget so a walk that is merely slow still
 * gets to return its own typed decline rather than being pre-empted here.
 */
export const CHUNKED_OBJECT_PLAN_TIMEOUT_MS = ISOBMFF_LAYOUT_TIMEOUT_MS + 500;

const PLAN_TIMED_OUT = Symbol("chunked-object-plan-timeout");

async function locateWithDeadline(
	walk: Promise<IsobmffLayoutResult>,
	file: Blob,
	timeoutMs: number,
	telemetry: UploadTelemetry
): Promise<IsobmffLayoutResult> {
	let timer: ReturnType<typeof setTimeout> | undefined;
	try {
		const raced = await Promise.race([
			walk,
			new Promise<typeof PLAN_TIMED_OUT>((resolve) => {
				timer = setTimeout(() => resolve(PLAN_TIMED_OUT), timeoutMs);
			})
		]);
		if (raced !== PLAN_TIMED_OUT) return raced;
		// The walk is still pending and stays pending; nothing here can cancel a hung Blob read. It
		// holds only its own reader, so it is garbage after this returns. Named explicitly because a
		// silent "no offer" here is indistinguishable from an ordinary decline in the logs.
		telemetry.logWarn("upload.chunkedObject.boxWalkTimedOut", {
			fileSize: file.size,
			timeoutMs
		});
		return {
			ok: false,
			reason: "timeout",
			detail: `plan budget ${timeoutMs}ms`
		};
	} catch (error) {
		// `locateIsobmffLayout` is contractually throwless, but an injected locate (or a hostile
		// Blob) is not. A decline only ever means "upload as multipart, like today".
		telemetry.logWarn("upload.chunkedObject.boxWalkThrew", { fileSize: file.size }, error);
		return {
			ok: false,
			reason: "read-error",
			detail: error instanceof Error ? error.message : String(error)
		};
	} finally {
		if (timer !== undefined) clearTimeout(timer);
	}
}

/**
 * Decide whether this selected video FILE can be offered as a chunked-object upload (plan 3a
 * decision D3) and, if so, what to send. Returns null on every decline — the caller then mints
 * exactly today's multipart body, so a decline is never an upload failure.
 *
 * `telemetry` is required: the shared package has no app sink, and every decline reason has to be
 * visible somewhere or an app-wide "nothing is ever offered" regression is invisible.
 */
/**
 * The runtime config object the kill switch lives on, read defensively.
 *
 * EXPORTED because the two app initiators need the SAME reading when they report that an offered
 * chunked-object transport was not selected: a `notSelected` line that cannot say whether the kill switch
 * is on leaves the two causes of a zero adoption rate — "the operator disabled it" and "the server flag is
 * off" — indistinguishable from the client side, which is the only side an app team can see.
 *
 * A worker/SSR context has no `window`; reading it unguarded would throw where the caller only ever wanted
 * "no kill switch configured".
 */
export function readChunkedObjectRuntimeEnv(): unknown {
	try {
		return typeof window !== "undefined" ? (window as Window & { env?: unknown }).env : undefined;
	} catch {
		return undefined;
	}
}

export async function planChunkedObjectVideo(
	file: File,
	options: {
		telemetry: UploadTelemetry;
		chunkSize?: number;
		env?: unknown;
		locate?: typeof locateIsobmffLayout;
		timeoutMs?: number;
	}
): Promise<ChunkedObjectVideoIntent | null> {
	try {
		return await derivePlan(file, options);
	} catch (error) {
		// R2a reliability audit (LSP-17653). BOTH app initiators `await planChunkedObjectVideo(...)`
		// unwrapped inside the target-initiation path, so anything this function rejects with fails the
		// whole video upload — with every server flag off, and for a file the multipart lane would have
		// taken without complaint. Planning is a pure optimisation: every OTHER decline already returns
		// null and mints today's multipart body, and a throw has to reach the same place.
		//
		// `derivePlan` is total by inspection today (`locateIsobmffLayout` is caught, and the manifest,
		// offer and ordering helpers all return typed results), so what lands here is what inspection
		// cannot cover: a hostile or wrapped `File` whose own accessors throw, and the caller's telemetry
		// sink. That is exactly why the catch is at the boundary rather than around one suspect call.
		logPlanFailureSafely(options.telemetry, error);
		return null;
	}
}

/**
 * Log the boundary failure WITHOUT being able to re-throw from the handler.
 *
 * CLAUDE.md "Diagnostic logging when fixing silent failures": a null return here is indistinguishable
 * from an ordinary decline, so the event is the only way anyone learns the plan threw. But the sink is
 * itself one of the two things that can have thrown, and a logger that throws inside the catch would
 * put the rejection straight back — so its failure is swallowed here and nowhere else.
 */
function logPlanFailureSafely(telemetry: UploadTelemetry, error: unknown): void {
	try {
		telemetry.logWarn("upload.chunkedObject.planThrew", { message: error instanceof Error ? error.message : String(error) }, error);
	} catch {
		// Nothing left to report it with. The upload proceeds on the multipart lane regardless.
	}
}

async function derivePlan(
	file: File,
	options: {
		telemetry: UploadTelemetry;
		chunkSize?: number;
		env?: unknown;
		locate?: typeof locateIsobmffLayout;
		timeoutMs?: number;
	}
): Promise<ChunkedObjectVideoIntent | null> {
	const telemetry = options.telemetry;
	const chunkSize = options.chunkSize ?? CHUNK_SIZE;
	const env = options.env ?? readChunkedObjectRuntimeEnv();
	// The kill switch is read BEFORE the box walk, not after. It is the operator's emergency control
	// for this lane, and the thing it most needs to stop is the walk itself: on a cloud placeholder
	// file whose `Blob.slice().arrayBuffer()` never settles, every video upload start otherwise pays
	// the full CHUNKED_OBJECT_PLAN_TIMEOUT_MS stall before the switch is even consulted. The `offer`
	// event is still emitted with the same `kill-switch` reason, so the adoption dashboards keep
	// seeing the decline; only the file I/O is skipped.
	const killSwitchDisabled = readChunkedObjectOfferKillSwitch(env);
	if (killSwitchDisabled) {
		telemetry.logInfo("upload.chunkedObject.offer", {
			fileSize: file.size,
			offered: false,
			reason: "kill-switch"
		});
		return null;
	}
	const startedAt = Date.now();
	// LSP-22818: the whole header walk takes one turn of the Android picked-file read gate, so its deadline
	// starts when the turn begins and queueing behind other reads never declines a local video. The gate stays
	// held until the walk itself settles, so a walk still reading after its deadline never overlaps the next read.
	const locate = options.locate ?? locateIsobmffLayout;
	const layout = await withPickedFileReadTurn(() => {
		// The executor turns a synchronous throw from an injected locate into a rejection, which becomes a decline.
		const walk = new Promise<IsobmffLayoutResult>((resolve) => resolve(locate(blobByteReader(file))));
		return { result: locateWithDeadline(walk, file, options.timeoutMs ?? CHUNKED_OBJECT_PLAN_TIMEOUT_MS, telemetry), settled: walk };
	});
	telemetry.logInfo("upload.chunkedObject.boxWalk", {
		fileSize: file.size,
		fileType: file.type,
		durationMs: Date.now() - startedAt,
		outcome: layout.ok ? layout.layout.kind : `declined:${layout.reason}`,
		reads: layout.ok ? layout.layout.reads : undefined,
		topLevelBoxCount: layout.ok ? layout.layout.topLevelBoxCount : undefined
	});
	const decision = shouldOfferChunkedObject({
		// Already handled above (this is necessarily `false` here); passed through rather than hardcoded
		// so the decision helper stays the single place that orders the decline reasons AND this call
		// cannot drift from the reading that actually gated the early return.
		killSwitchDisabled,
		layout,
		manifest: buildChunkManifest(file.size, chunkSize)
	});
	telemetry.logInfo("upload.chunkedObject.offer", {
		fileSize: file.size,
		offered: decision.offer,
		reason: decision.offer ? undefined : decision.reason
	});
	if (!decision.offer) return null;
	const totalChunks = decision.manifest.parts.length;
	const priorityChunkIndexes = headerFirstChunkOrder(decision.layout, chunkSize, totalChunks);
	// Header-first is a PREFERENCE the server never validates: it re-derives the layout from the
	// landed `.part` objects and treats a late moov as normal. What it is not allowed to be is a
	// partial order — the chunk loop walks this list and would silently never send the missing
	// chunks. A short/long permutation is a programming error in `headerFirstChunkOrder`, so decline
	// rather than start an upload that can never complete.
	if (priorityChunkIndexes.length !== totalChunks) {
		telemetry.logWarn("upload.chunkedObject.priorityOrderInvalid", {
			fileSize: file.size,
			totalChunks,
			priorityChunkCount: priorityChunkIndexes.length
		});
		return null;
	}
	return {
		manifest: decision.manifest,
		layout: decision.layout,
		priorityChunkIndexes,
		chunkSizeBytes: chunkSize,
		killSwitchDisabled
	};
}
